import React, { useState, useContext, useCallback } from 'react';
import PropTypes from 'prop-types';

import { isTrialProduct } from 'ggApp/shared/components/cards/ProductDetailsCard/TrialCard/skuList';
import { OrderContext } from 'ggApp/shared/context/Order/OrderContext';
import { Translator, PureTranslator } from 'ggApp/modules/translator/components';
import PriceSuffix from 'ggApp/shared/components/cards/ProductDetailsCard/PriceSuffix';
import RentalPlanSelector from 'ggApp/modules/rentalPlan/components/RentalPlanSelector';
import { PRODUCT_CTA_SUBSCRIBE_CLASS } from 'ggApp/modules/tracking/constants';
import { OrderModes } from 'ggApp/shared/context/Order/types';

import {
    MixActionsWrapper,
    OriginalActionsWrapper,
    MarketPrice,
    PlanPriceDetails,
    OldPrice,
    Price,
    TaxText,
    GroverCareBadge,
    DeliveryBadge,
    EnergyLabelBadge,
    RentalPeriodBadge,
    RentalPlanSelectorWrap,
    TrialDescription,
    ColorSelector,
    Cta,
    CtaText,
    MixableProductDetails,
    FlexibleProductDetails,
    LongRevocationBadge,
} from './styles';

const RentalActions = ({
    mixPDP,
    marketPrice,
    hasRentalPlans,
    oldPrice,
    price,
    tooltipText,
    currentlyAvailable,
    withFreeShipping,
    deliveryEstimate,
    isFlexibleProduct,
    selectedMinDuration,
    selectedPlan,
    withColorSelector,
    selectedPropertyItemIndex,
    propertiesList,
    selectOption,
    onCtaClick,
    ctaTk,
    currentVariant,
    productCategoryIds,
    productSku,
    productId,
    preorderProduct,
    onClickMixCard,
    productFreeTrialFlag,
    activeStore,
    longRevocationPeriodTestData,
    currentProductPromotions,
}) => {
    const { cart, orderMode } = useContext(OrderContext);
    const [activeCard, setActiveCard] = useState(orderMode);
    const {
        id: variantId,
        sku: variantSku,
        pictures: [picture],
        core_attribute: coreAttribute,
        name,
    } = currentVariant || { pictures: [] };
    const {
        image_urls: { mini: image },
    } = picture || { image_urls: {} };

    const { subCategoryId, categoryId } = productCategoryIds;
    const hasItemsInCart = !!cart?.items?.length;
    const energyRating = currentProductPromotions?.energyLabel?.energyRating;

    const withTrialDescription =
        activeStore?.code === 'de' &&
        productFreeTrialFlag &&
        selectedPlan?.minimumMonths > 1 &&
        isTrialProduct(productSku);

    const onOpenMixCard = useCallback(() => {
        if (typeof onOpenMixCard === 'function' && activeCard !== OrderModes.Mix) {
            onClickMixCard({ mixData: { count: cart?.items?.length ?? null } });
        }
        setActiveCard(OrderModes.Mix);
    }, [onClickMixCard, activeCard, cart]);

    return mixPDP ? (
        <MixActionsWrapper data-testid="mix-rental-actions">
            {withColorSelector && selectedPropertyItemIndex !== -1 ? (
                <ColorSelector
                    selectedIndex={selectedPropertyItemIndex}
                    colorsList={propertiesList}
                    onOptionSelect={selectOption}
                />
            ) : null}

            <MixableProductDetails
                active={activeCard === OrderModes.Mix && currentVariant?.mixable}
                selectFlow={onOpenMixCard}
                disabled={!(currentVariant && currentVariant.mixable && currentlyAvailable)}
                activeProduct={{
                    id: variantId,
                    image,
                    categoryId,
                    subCategoryId,
                    productSku,
                    variantSku,
                    coreAttribute,
                    name,
                    productId,
                }}
                onCtaClick={onCtaClick}
                inFlexCartFlow={orderMode === OrderModes.Flex && hasItemsInCart}
                deliveryEstimate={deliveryEstimate}
            />

            <FlexibleProductDetails
                active={activeCard === OrderModes.Flex || !currentVariant?.mixable}
                selectFlow={() => setActiveCard(OrderModes.Flex)}
                selectedMinDuration={selectedMinDuration}
                oldPrice={oldPrice}
                price={price}
                onCtaClick={() => onCtaClick('FLEX')}
                canSubscribe={currentlyAvailable && hasRentalPlans}
                inMixCartFlow={orderMode === 'MIX' && hasItemsInCart}
                preorderProduct={preorderProduct}
                deliveryEstimate={deliveryEstimate}
                withTrialDescription={withTrialDescription}
                ctaTk={ctaTk}
            />
        </MixActionsWrapper>
    ) : (
        <OriginalActionsWrapper data-testid="original-rental-actions">
            {!!marketPrice ? (
                <MarketPrice data-testid="market-price-id">
                    <Translator tk="PRODUCT_CARD_MARKET_PRICE_PREFIX" />
                    {marketPrice}
                </MarketPrice>
            ) : null}
            {hasRentalPlans ? (
                <PlanPriceDetails>
                    {oldPrice && <OldPrice>{oldPrice}</OldPrice>}
                    <Price withOldPrice={!!oldPrice}>{price}</Price>
                    <TaxText>
                        <PriceSuffix />
                    </TaxText>
                </PlanPriceDetails>
            ) : null}
            <GroverCareBadge tooltipText={tooltipText} withFreeService />
            {longRevocationPeriodTestData && longRevocationPeriodTestData.variantKey ? (
                <LongRevocationBadge
                    revocationPeriodVariant={longRevocationPeriodTestData.variantKey}
                />
            ) : null}
            {currentlyAvailable && hasRentalPlans ? (
                <DeliveryBadge
                    withFreeShipping={withFreeShipping}
                    deliveryEstimation={deliveryEstimate}
                    preorderProduct={preorderProduct}
                />
            ) : null}
            {energyRating ? (
                <EnergyLabelBadge
                    energyRating={energyRating}
                    productId={productId}
                    activeLanguage={activeStore?.activeLanguage}
                />
            ) : null}
            {hasRentalPlans && !isFlexibleProduct ? (
                <>
                    <RentalPeriodBadge />
                    <RentalPlanSelectorWrap>
                        <RentalPlanSelector selectedMinDuration={selectedMinDuration} />
                    </RentalPlanSelectorWrap>
                </>
            ) : null}
            {withTrialDescription ? <TrialDescription price={price} /> : null}
            {withColorSelector && selectedPropertyItemIndex !== -1 ? (
                <ColorSelector
                    selectedIndex={selectedPropertyItemIndex}
                    colorsList={propertiesList}
                    onOptionSelect={selectOption}
                />
            ) : null}
            {currentlyAvailable && hasRentalPlans ? (
                <Cta
                    className={PRODUCT_CTA_SUBSCRIBE_CLASS}
                    fullWidth
                    color="red"
                    onClick={() => onCtaClick('FLEX')}
                >
                    <CtaText>
                        <PureTranslator tk={ctaTk} />
                    </CtaText>
                </Cta>
            ) : null}
        </OriginalActionsWrapper>
    );
};

export default RentalActions;

RentalActions.propTypes = {
    mixPDP: PropTypes.bool,
    marketPrice: PropTypes.string,
    hasRentalPlans: PropTypes.bool,
    oldPrice: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
    price: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    tooltipText: PropTypes.string,
    productSku: PropTypes.string.isRequired,
    currentlyAvailable: PropTypes.bool,
    withFreeShipping: PropTypes.bool,
    deliveryEstimate: PropTypes.shape({}),
    isFlexibleProduct: PropTypes.bool,
    selectedMinDuration: PropTypes.number,
    selectedPlan: PropTypes.shape({
        planDescription: PropTypes.string,
        minimumMonths: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }).isRequired,
    withColorSelector: PropTypes.bool,
    selectedPropertyItemIndex: PropTypes.number.isRequired,
    propertiesList: PropTypes.arrayOf(PropTypes.object).isRequired,
    selectOption: PropTypes.func,
    onCtaClick: PropTypes.func,
    onClickMixCard: PropTypes.func,
    ctaTk: PropTypes.string,
    currentVariant: PropTypes.shape({
        mixable: PropTypes.bool,
        id: PropTypes.number,
        pictures: PropTypes.arrayOf(PropTypes.shape({})),
    }).isRequired,
    productCategoryIds: PropTypes.shape({}).isRequired,
    currentProductPromotions: PropTypes.shape({
        energyLabel: PropTypes.shape({
            energyRating: PropTypes.string,
        }),
    }),
};

RentalActions.defaultProps = {
    mixPDP: false,
    marketPrice: undefined,
    hasRentalPlans: undefined,
    oldPrice: undefined,
    price: undefined,
    tooltipText: undefined,
    currentlyAvailable: undefined,
    withFreeShipping: undefined,
    deliveryEstimate: {},
    isFlexibleProduct: false,
    selectedMinDuration: null,
    withColorSelector: undefined,
    selectOption: () => {},
    onCtaClick: () => {},
    onClickMixCard: () => {},
    ctaTk: undefined,
    currentProductPromotions: undefined,
};
